import React, { useEffect, useState } from 'react';
import { Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Input } from '@/components/ui/input';
import { formatApiError } from '@/utils/apiError';

/** Editable unit cost (ex VAT): shows Xero purchase price by default; typing saves a manual override on blur/Enter. */
export function CostCell({ product, onSaved }) {
  const [val, setVal] = useState(product.cost_price != null ? String(product.cost_price) : '');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(product.cost_price != null ? String(product.cost_price) : ''); }, [product.cost_price]);

  const save = async (cost) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/product-pricing/${product.id}/cost`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ cost_price: cost }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(body) || 'Save failed');
      onSaved(body);
      toast.success(cost ? `${product.name}: cost set to £${Number(cost).toFixed(2)}` : `${product.name}: back to Xero cost`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const commit = () => {
    const n = val === '' ? null : Number(val);
    if ((n ?? null) === (product.cost_price ?? null)) return;
    save(n);
  };

  const xero = product.xero_cost_price;
  const manual = product.cost_price != null;
  return (
    <div className="flex items-center justify-end gap-1" data-testid={`pricing-cost-${product.id}`}>
      {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
      {manual && xero != null && (
        <button type="button" onClick={() => save(null)} className="p-1 text-gray-400 hover:text-sky-700" title={`Clear override and use Xero cost £${Number(xero).toFixed(2)}`} aria-label="Use Xero cost" data-testid={`pricing-cost-reset-${product.id}`}><RotateCcw className="w-3.5 h-3.5" /></button>
      )}
      <Input type="number" step="0.01" min="0" value={val} onChange={(e) => setVal(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
        placeholder={xero != null ? Number(xero).toFixed(2) : 'no cost'} data-testid={`pricing-cost-input-${product.id}`}
        className={`h-8 w-24 text-right ${manual ? 'border-amber-300 bg-amber-50/50 font-semibold' : xero != null ? 'text-gray-700' : 'border-rose-200 placeholder:text-rose-400'}`}
        title={manual ? 'Manual override (ex VAT)' : xero != null ? `Xero purchase price £${Number(xero).toFixed(2)} — type to override` : 'No cost in Xero — type one here'} />
    </div>
  );
}
